import { useEffect, useMemo, useState } from 'react';
import { FinanceiroService } from './Service';
import { formatMoneyBRL, toDateInputValue } from './utils';

const service = new FinanceiroService();

type ReceivableStatus = 'OPEN' | 'PAID' | 'CANCELED' | '';
type PaymentMethod = 'CASH' | 'PIX' | 'DEBIT' | 'CREDIT' | 'TRANSFER' | 'OTHER';

export default function Receber() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [baixarOpen, setBaixarOpen] = useState(false);
    const [baixarRow, setBaixarRow] = useState<any>(null);
    const [baixarAccountId, setBaixarAccountId] = useState<string>('');
    const [baixarPaymentMethod, setBaixarPaymentMethod] = useState<PaymentMethod>('PIX');
    const [baixarPaidAt, setBaixarPaidAt] = useState<string>(toDateInputValue(new Date()));

    const defaultTo = useMemo(() => new Date(), []);
    const defaultFrom = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    }, []);

    const [status, setStatus] = useState<ReceivableStatus>('OPEN');
    const [startAt, setStartAt] = useState<string>(toDateInputValue(defaultFrom));
    const [endAt, setEndAt] = useState<string>(toDateInputValue(defaultTo));

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const [receberRes, accountsRes] = await Promise.all([
                service.receberFindAll({
                    status: status || undefined,
                    startAt,
                    endAt,
                    page: 1,
                    limit: 100,
                }),
                service.accountsFindAll(),
            ]);
            setRows(receberRes?.data?.data || []);
            setAccounts(Array.isArray(accountsRes?.data) ? accountsRes.data : []);
        } catch (e: any) {
            setError(String(e?.response?.data?.message || 'Erro ao carregar contas a receber'));
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openBaixar = (row: any) => {
        setBaixarRow(row);
        setBaixarPaidAt(toDateInputValue(new Date()));

        const defaultAcc =
            accounts.find((a) => a?.isDefault) || accounts[0];
        setBaixarAccountId(defaultAcc?._id ? String(defaultAcc._id) : '');
        setBaixarPaymentMethod('PIX');
        setBaixarOpen(true);
    };

    const markPaid = async () => {
        if (!baixarRow?._id) return;
        try {
            await service.receberMarkPaid({
                id: String(baixarRow._id),
                paidAt: baixarPaidAt,
                accountId: baixarAccountId || undefined,
                paymentMethod: baixarPaymentMethod,
            });
            setBaixarOpen(false);
            setBaixarRow(null);
            await load();
        } catch (e) {
            console.error(e);
            await load();
        }
    };

    const cancel = async (id: string) => {
        try {
            await service.receberCancel({ id });
            await load();
        } catch (e) {
            console.error(e);
            await load();
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="card bg-base-100 shadow">
                <div className="card-body py-3">
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">Status</span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-full md:w-44"
                                value={status}
                                onChange={(e) => setStatus(e.target.value as any)}
                            >
                                <option value="OPEN">Em aberto</option>
                                <option value="PAID">Pago</option>
                                <option value="CANCELED">Cancelado</option>
                                <option value="">Todos</option>
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">Venc. de</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={startAt}
                                onChange={(e) => setStartAt(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">Venc. até</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={endAt}
                                onChange={(e) => setEndAt(e.target.value)}
                            />
                        </div>
                        <button className="btn btn-sm btn-primary md:ml-auto" onClick={load}>
                            Atualizar
                        </button>
                    </div>

                    {error && (
                        <div className="alert alert-error mt-3">
                            <span>{error}</span>
                        </div>
                    )}
                </div>
            </div>

            {baixarOpen && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Baixar recebimento</h3>
                        <div className="mt-3 flex flex-col gap-3">
                            <div>
                                <div className="text-sm opacity-70">Conta</div>
                                <select
                                    className="select select-bordered select-sm w-full"
                                    value={baixarAccountId}
                                    onChange={(e) => setBaixarAccountId(e.target.value)}
                                >
                                    <option value="">Selecione</option>
                                    {accounts.map((a) => (
                                        <option key={String(a._id)} value={String(a._id)}>
                                            {a.name} {a.isDefault ? '(Padrão)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <div className="text-sm opacity-70">Forma</div>
                                <select
                                    className="select select-bordered select-sm w-full"
                                    value={baixarPaymentMethod}
                                    onChange={(e) => setBaixarPaymentMethod(e.target.value as any)}
                                >
                                    <option value="CASH">Dinheiro</option>
                                    <option value="PIX">Pix</option>
                                    <option value="DEBIT">Débito</option>
                                    <option value="CREDIT">Crédito</option>
                                    <option value="TRANSFER">Transferência</option>
                                    <option value="OTHER">Outro</option>
                                </select>
                            </div>
                            <div>
                                <div className="text-sm opacity-70">Data</div>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm w-full"
                                    value={baixarPaidAt}
                                    onChange={(e) => setBaixarPaidAt(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setBaixarOpen(false);
                                    setBaixarRow(null);
                                }}
                            >
                                Fechar
                            </button>
                            <button className="btn btn-primary" disabled={!baixarAccountId} onClick={markPaid}>
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    <div className="overflow-x-auto">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Descrição</th>
                                    <th>Cliente</th>
                                    <th>Vencimento</th>
                                    <th>Status</th>
                                    <th className="text-right">Valor</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6}>Carregando...</td>
                                    </tr>
                                ) : rows.length ? (
                                    rows.map((r) => (
                                        <tr key={String(r._id)}>
                                            <td>{r.description || '-'}</td>
                                            <td>{r.clientId || '-'}</td>
                                            <td>{r.dueAt ? new Date(r.dueAt).toLocaleDateString('pt-BR') : '-'}</td>
                                            <td>{r.status}</td>
                                            <td className="text-right">{formatMoneyBRL(r.amount || 0)}</td>
                                            <td className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        className="btn btn-xs"
                                                        disabled={r.status !== 'OPEN'}
                                                        onClick={() => openBaixar(r)}
                                                    >
                                                        Baixar
                                                    </button>
                                                    <button
                                                        className="btn btn-xs btn-ghost"
                                                        disabled={r.status !== 'OPEN'}
                                                        onClick={() => cancel(String(r._id))}
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6}>Nenhum registro</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
